import {Tooltip} from '@mui/material'
import {Box, Loading, Skeleton, Typography} from '@viptech/react-components'
import {useEffect, useRef, useState} from 'react'
import ReactApexChart from 'react-apexcharts'
import Dropdown from 'src/components/dropdown/Dropdown'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'

type ColumnChartData = {
  name: string
  data: Array<number | null>
  type: string
  color?: string
}

type ColumnChartProps = {
  title: string
  loading: boolean
  data: Array<ColumnChartData>
  labelsXaxis: Array<string | number | Array<string>> | Array<Date> | Array<Date | string> | undefined
  height?: string
  refetching?: boolean
  subtitle?: string
  setType: (type: string) => void
}

const GAP = 52

const MOCK_SIZES = [
  60, 44, 47, 89, 146, 26, 142, 86, 83, 135, 115, 28, 149, 52, 43, 93, 80, 48, 100, 37, 144, 145, 109, 71, 83, 102, 26, 135, 150, 116, 70, 103, 149, 49, 133,
  78, 85, 64, 29, 85, 110, 132, 31, 101, 134, 44, 122, 125, 123, 45,
]

function DashboardColumnAndLinesChartsCard({title, loading, refetching, data, height = '420px', labelsXaxis, subtitle, setType}: ColumnChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const {styledTheme} = usePersonalizedThemeByCompany()
  const [type, setTypeChart] = useState({id: 'day', label: 'Diário'})

  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - GAP)
      }
    }

    updateContainerWidth()

    window.addEventListener('resize', updateContainerWidth)
    const observer = new ResizeObserver(updateContainerWidth)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateContainerWidth)
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth - GAP)
    }
  }, [loading, refetching])

  const formatLabelXaxis = () => {
    if (type.id === 'day') return 'dd/MM/yy'
    if (type.id === 'month') return 'MMM'
    if (type.id === 'year') return 'yyyy'
  }

  const maxValue = Math.max(...data.flatMap((item) => item.data).filter((value): value is number => value !== null))
  const opts: ApexCharts.ApexOptions = {
    series: data,
    chart: {
      type: 'line',
      fontFamily: "'Inter', 'sans-serif",
      defaultLocale: 'pt',
      locales: [
        {
          name: 'pt',
          options: {
            months: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
            shortMonths: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
          },
        },
      ],
      toolbar: {
        show: false,
      },
    },
    stroke: {
      curve: 'smooth',
      width: 1.5,
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        distributed: false,
        rangeBarGroupRows: false,
        hideZeroBarsWhenGrouped: false,
      },
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0],
      background: {
        enabled: false,
      },
      style: {
        colors: ['#1570EF'],
      },
      textAnchor: 'middle',
      offsetX: 0,
      offsetY: -10,
    },
    legend: {
      show: false,
      markers: {
        shape: 'line',
      },
      itemMargin: {
        vertical: 10,
      },
    },
    xaxis: {
      categories: labelsXaxis ?? [],
      type: type.id === 'week' ? 'category' : 'datetime',
      labels: {
        trim: false,
        hideOverlappingLabels: true,
        rotate: 0,
        style: {
          fontSize: '12px',
          fontWeight: '500',
        },
        format: formatLabelXaxis(),
        showDuplicates: false,
      },
    },
    responsive: [
      {
        breakpoint: 968,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '80%',
            },
          },
        },
      },
    ],
    grid: {
      show: true,
    },
    yaxis: {
      min: 0,
      max: maxValue,
    },
    tooltip: {
      enabled: true,
      theme: 'light',
    },
    noData: {
      text: 'Nenhum dado foi encontrado',
      align: 'center',
      verticalAlign: 'top',
      offsetX: 5,
      offsetY: 15,
      style: {
        color: '#8E8E8E',
        fontSize: '16px',
        fontFamily: "'Inter', 'sans-serif",
      },
    },
  }

  return (
    <Box
      ref={containerRef}
      position='relative'
      backgroundColor='white'
      borderRadius='16px'
      width='100%'
      height={height}
      style={{boxShadow: '0px 5px 8px 0px #00000026'}}>
      <Box display='flex' direction='column' padding='24px' rowGap='20px' align='center' flex={1}>
        <Box display='flex' width='100%' align='center' justifyContent='center' position='relative'>
          <Box display='flex' direction='column' align='center' rowGap='6px'>
            <Typography variant='span' size='16px' color={styledTheme.dashboard.colors.title} children={title} weight={600} />
            <Typography
              variant='span'
              size='12px'
              color={styledTheme.dashboard.colors.title}
              style={{fontStyle: 'italic'}}
              children={subtitle?.replace('.', '')}
            />
          </Box>

          <Box position='absolute' right='10px'>
            <Dropdown
              labelFontSize='12px'
              borderColor='#D0D5DD'
              fontSize='12px'
              height='32px'
              width='135px'
              placeholder=''
              onChangeSelected={(e) => {
                setTypeChart(e)
                setType(e.id)
              }}
              selected={type}
              items={[
                {id: 'day', label: 'Diário'},
                {id: 'week', label: 'Semanal'},
                {id: 'month', label: 'Mensal'},
                {id: 'year', label: 'Anual'},
              ]}
            />
          </Box>
        </Box>

        {refetching && !loading && (
          <Tooltip title='Buscando atualizações' placement='top'>
            <Box position='absolute' right='24px' display='flex' columnGap='8px'>
              <Loading size='20px' />
            </Box>
          </Tooltip>
        )}

        {loading ? (
          <Box width={'100%'} height={'300px'} display='flex' align='flex-end' columnGap='24px' paddingRight='24px' backgroundColor='white' overflow='hidden'>
            <Box>
              {new Array(10).fill(0).map((_, index) => (
                <Skeleton width='12px' height='8px' borderRadius='4px' key={index} />
              ))}
            </Box>
            {new Array(30).fill(0).map((_, index) => (
              <Skeleton width='48px' height={`${MOCK_SIZES[index]}px`} borderRadius='4px' key={index} />
            ))}
          </Box>
        ) : (
          <ReactApexChart options={opts} series={opts.series} type='line' height={300} width={containerWidth} />
        )}
      </Box>
    </Box>
  )
}

export default DashboardColumnAndLinesChartsCard
